import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Card } from "../../../components/tailwind/card";
import { Badge } from "../../../components/tailwind/badge";
import { Gift, Search } from "lucide-react";
import { showAlert } from "../../../store/alertSlice";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "../../tailwind/select";

export const TemplateSelector = ({ templates = [], selectedId, onSelect, hideStepNumber = false }) => {
  const dispatch = useDispatch();

  const templateList = Array.isArray(templates) ? templates : [];
  const selectedTemplate = templateList.find(t => t.id === selectedId);

  const categories = [
    { key: 'utility', label: 'Utility Templates', color: 'text-blue-600', icon: '📋' },
    { key: 'authentication', label: 'Authentication Templates', color: 'text-indigo-600', icon: '🔐' },
    { key: 'marketing', label: 'Marketing Templates', color: 'text-purple-600', icon: '📢' }
  ];

  const mediaTypeBadgeConfigs = {
    image: { label: 'Image', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-100' },
    video: { label: 'Video', bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-100' },
    document: { label: 'Document', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
    audio: { label: 'Audio', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-100' }
  };

  const getMediaTypeFromHeader = (header) => {
    if (!header) return null;
    try {
      const parsed = typeof header === 'string' ? JSON.parse(header) : header;
      const format = String(parsed.format || '').toLowerCase();
      return format && mediaTypeBadgeConfigs[format] ? format : null;
    } catch (e) {
      return null;
    }
  };

  const filteredTemplates = useMemo(() => {
    return [...templateList].sort((a, b) => {
      const dateA = new Date(a.updatedAt || 0).getTime();
      const dateB = new Date(b.updatedAt || 0).getTime();
      return dateB - dateA;
    });
  }, [templateList]);

  const renderBadge = (t) => {
    const header = t?.components?.HEADER;
    const mediaType = getMediaTypeFromHeader(header);
    const mediaConfig = mediaType ? mediaTypeBadgeConfigs[mediaType] : null;
    const isApproved = String(t.status || '').toLowerCase() === 'approved';

    if (!mediaType && !isApproved) {
      return null;
    }

    return (
      <div className="flex items-center gap-1">
        {mediaConfig && (
          <Badge variant="secondary" className={`${mediaConfig.bg} ${mediaConfig.text} ${mediaConfig.border} text-[10px]`}>
            {mediaConfig.label}
          </Badge>
        )}
        {isApproved && (
          <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-100 text-[10px]">Approved</Badge>
        )}
      </div>
    );
  };

  return (
    <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        {!hideStepNumber && (
          <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold">
            2
          </div>
        )}
        <div>
          <h3 className="font-semibold text-slate-900 dark:text-white text-lg">Choose Template</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Only Approved Template</p>
        </div>
      </div>

      <div className="space-y-4">
        <Select
          value={selectedId ? String(selectedId) : ""}
          onValueChange={(val) => {
            if (val) {
              const template = templateList.find(t => String(t.id) === val);
              const isApproved = template && String(template.status || '').toLowerCase() === 'approved';

              if (!isApproved) {
                dispatch(showAlert({
                  message: 'Only approved templates can be used to send messages. Please select an approved template.',
                  type: 'error'
                }));
                return;
              }
            }
            onSelect(val || null);
          }}
          disabled={templateList.length === 0}
        >
          <SelectTrigger className="bg-slate-50 dark:bg-slate-950 border-2 border-slate-200 dark:border-slate-800 rounded-lg text-sm hover:border-blue-300 dark:hover:border-blue-700 focus:border-blue-500 dark:focus:border-blue-500 transition-colors">
            <SelectValue placeholder="Select a message template..." />
          </SelectTrigger>

          <SelectContent className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-xl">
            {categories.map(cat => {
              const items = filteredTemplates.filter(t => String(t.category || '').toLowerCase() === cat.key);
              if (!items || items.length === 0) return null;
              return (
                <SelectGroup key={cat.key}>
                  <SelectLabel className={`${cat.color} mt-3 mb-2 text-xs font-semibold flex items-center gap-2 px-2`}>
                    <span>{cat.icon}</span>
                    {cat.label}
                    <Badge variant="outline" className="ml-auto text-[10px]">{items.length}</Badge>
                  </SelectLabel>
                  {items.map(t => (
                    <SelectItem
                      key={t.id}
                      value={String(t.id)}
                      className="py-3 px-2 hover:bg-blue-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between gap-4 w-full">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{t.name}</p>
                          </div>
                        </div>
                        {renderBadge(t)}
                      </div>
                    </SelectItem>
                  ))}
                </SelectGroup>
              );
            })}
          </SelectContent>
        </Select>

        {templateList.length === 0 && (
          <div className="text-center py-8">
            <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
              <Search className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">No templates available</p>
            <p className="text-xs text-slate-500 dark:text-slate-500">Templates are loading or not available yet.</p>
          </div>
        )}
      </div>

      {selectedTemplate?.category === "marketing" && (
        <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
          <Gift className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Premium Message Type</p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">Marketing messages incur higher charges per delivery.</p>
          </div>
        </div>
      )}
    </Card>
  );
};
